<template>
  <b-card>
    <b-overlay
      :show="loading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <b-form
        @submit.prevent
        @submit="setFinanceSetting"
      >
        <b-tabs>
          <!-- Tab 1: Strict Mode -->
          <b-tab
            title="Strict Mode"
            active
          >
            <b-card title="Strict Mode">
              <b-form-radio-group
                v-model="form.strict.mode"
                :options="radioOptions"
                class="demo-inline-spacing mb-1"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
            </b-card>
            <p class="text-muted ml-2">
              If this mode is on, invoices cannot be created until the Agent, Branch or rider deposits are fully settled and approved.
            </p>
          </b-tab>

          <!-- Tab 2: Finance Cron Job -->
          <b-tab title="Finance Cron Job">
            <b-card title="Finance Cron Job">
              <b-form-radio-group
                v-model="form.cronjob.enabled"
                :options="radioOptions"
                class="demo-inline-spacing mb-1"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
              <b-form-group
                label="Runtime (HH:MM)"
                label-for="runtime"
                class="mt-3"
              >
                <cleave
                  id="runtime"
                  v-model="form.cronjob.runtime"
                  :required="form.cronjob.enabled"
                  :disabled="!form.cronjob.enabled"
                  :raw="false"
                  :options="cleaveOptions.time"
                  placeholder="Enter runtime"
                  class="form-control"
                />
              </b-form-group>

              <!-- New Cron Job Settings -->
              <template v-if="form.cronjob.enabled">
                <b-row class="align-items-center">
                  <!-- Left Column -->
                  <b-col
                    md="6"
                    class="pr-2"
                  >
                    <b-form-group class="compact-group">
                      <label class="compact-label">Rider Deposit</label>
                      <b-form-radio-group
                        v-model="form.cronjob.rider_deposit.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>

                    <b-form-group class="compact-group">
                      <label class="compact-label">Branch Deposit</label>
                      <b-form-radio-group
                        v-model="form.cronjob.branch_deposit.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>

                    <b-form-group class="compact-group">
                      <label class="compact-label">Agency Deposit</label>
                      <b-form-radio-group
                        v-model="form.cronjob.agency_deposit.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>
                  </b-col>

                  <!-- Right Column -->
                  <b-col
                    md="6"
                    class="pl-2"
                  >
                    <b-form-group class="compact-group">
                      <label class="compact-label">Rider Commission</label>
                      <b-form-radio-group
                        v-model="form.cronjob.rider_commission.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>

                    <b-form-group class="compact-group">
                      <label class="compact-label">Agency Commission</label>
                      <b-form-radio-group
                        v-model="form.cronjob.agency_commission.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>

                    <b-form-group class="compact-group">
                      <label class="compact-label">Merchant Invoice</label>
                      <b-form-radio-group
                        v-model="form.cronjob.merchant_invoice.enabled"
                        :options="radioOptions"
                        class="compact-radio"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>
            </b-card>
            <p class="text-muted ml-2">
              Here you can specify whether you want the system to auto generate deposits and invoices and if yes, the runtime at which it should occur.
            </p>
          </b-tab>

          <!-- Tab 3: Weight Roundup Option -->
          <b-tab title="Weight Roundup Option">
            <b-card title="Weight Roundup Option">
              <b-form-radio-group
                v-model="form.round_up.enabled"
                :options="roundupOptions"
                class="mb-3"
                value-field="item"
                text-field="name"
                disabled-field="notEnabled"
              />
              <b-form-group
                label="Roundup Margin"
                label-for="weight"
                class="mt-4"
              >
                <b-form-input
                  v-model="form.round_up.value"
                  placeholder="Enter Weight Margin"
                  :required="form.round_up.enabled"
                  :disabled="!form.round_up.enabled"
                  min="0.1"
                  max="0.9"
                />
              </b-form-group>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                variant="outline-secondary"
                class="mt-1"
                @click="roundupReset"
              >
                Reset
              </b-button>
            </b-card>
            <p class="text-muted ml-2">
              If this option is enabled, the Weight will be rounded up for the delivery charge calculation. The default roundup margin is 0.5.
            </p>

            <!-- Override Actual Weight -->
            <template v-if="form.round_up.enabled">
              <b-card
                title="Override Actual Weight Roundup"
                class="mt-2"
              >
                <b-form-radio-group
                  v-model="form.override_round_up.enabled"
                  :options="roundupOptions"
                  class="mb-3"
                  value-field="item"
                  text-field="name"
                  disabled-field="notEnabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                If this option is enabled the rounded up weight will be saved overriding the actual weight. If the setting is disabled the actual weight will be saved but the Weight will be rounded up for the delivery charge calculation as per the roundup margin provided.
              </p>
            </template>
          </b-tab>

          <!-- Tab 4: Commission Invoice Setting -->
          <b-tab title="Commission Invoice Setting">
            <b-card title="Enable Rider Commission Invoice Setting">
              <b-form-radio-group
                v-model="form.commission_invoice.rider_commission_invoice.enabled"
                :options="radioOptions"
                class="demo-inline-spacing mb-1"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
            </b-card>
            <p class="text-muted ml-2">
              When this setting is enabled it allows the admins to mark rider commissions as paid
              for riders with deposits that are in both deposited and approved status.
            </p>

            <b-card
              title="Enable Agent Commission Invoice Setting"
              class="mt-2"
            >
              <b-form-radio-group
                v-model="form.commission_invoice.agency_commission_invoice.enabled"
                :options="radioOptions"
                class="demo-inline-spacing mb-1"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
            </b-card>
            <p class="text-muted ml-2">
              When this setting is enabled it allows the admins to mark agent commissions as paid
              for agents with deposits that are in both deposited and approved status.
            </p>
          </b-tab>
        </b-tabs>

        <!-- Submit Button -->
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-end">
            <b-button
              v-can="'business-profile-manage'"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              class="mt-1 custom-button-color"
            >
              Submit
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BButton,
  BCard,
  BOverlay,
  BFormRadioGroup,
  BFormInput,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Cleave from 'vue-cleave-component'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const FinanceSettingsRepository = RepositoryFactory.get('financeSetting')

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BButton,
    BCard,
    BOverlay,
    BFormRadioGroup,
    Cleave,
    BFormInput,
    BTabs,
    BTab,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      form: {
        strict: {
          mode: false,
        },
        cronjob: {
          enabled: false,
          runtime: '',
          rider_deposit: {
            enabled: true,
          },
          rider_commission: {
            enabled: true,
          },
          branch_deposit: {
            enabled: true,
          },
          agency_deposit: {
            enabled: true,
          },
          agency_commission: {
            enabled: true,
          },
          merchant_invoice: {
            enabled: true,
          },
        },
        round_up: {
          value: 0.5,
          enabled: false,
        },
        override_round_up: {
          enabled: true,
        },
        commission_invoice: {
          rider_commission_invoice: {
            enabled: false,
          },
          agency_commission_invoice: {
            enabled: false,
          },
        },
      },
      radioOptions: [
        { text: 'On', value: true },
        { text: 'Off', value: false },
      ],
      roundupOptions: [
        { item: true, name: 'On' },
        { item: false, name: 'Off' },
      ],
      overrideOptions: [
        { item: true, name: 'On' },
        { item: false, name: 'Off' },
      ],
      cleaveOptions: {
        time: {
          time: true,
          timePattern: ['h', 'm'],
        },
      },
    }
  },
  watch: {
    // eslint-disable-next-line func-names
    'form.round_up.enabled': function (roundupValue) {
      if (!roundupValue) {
        this.turnOffOverRide()
      }
    },
  },
  mounted() {
    this.readFinanceSetting()
  },
  methods: {
    async readFinanceSetting() {
      this.loading = true
      try {
        const { data } = (await FinanceSettingsRepository.getFinanceSettingsResource()).data
        this.form = data
        if (!this.form.round_up) {
          this.form.round_up = {
            value: 0.5,
            enabled: '',
          }
        }
        if (!this.form.override_round_up) {
          this.form.override_round_up = {
            enabled: '',
          }
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async setFinanceSetting() {
      this.loading = true
      try {
        const res = await FinanceSettingsRepository.updateFinanceSettingsResource(this.form)
        if (res.status === 200) {
          this.showSuccessMessage('Finance Settings Updated Successfully')
          window.location.reload()
        } else {
          this.showErrorMessage('Cannot Update Finance Settings')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    roundupReset() {
      this.form.round_up.value = 0.5
    },
    turnOffOverRide() {
      this.form.override_round_up.enabled = false
    },
  },
}
</script>

<style scoped>
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.compact-group {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.compact-label {
  min-width: 150px;
  margin-right: 10px;
  white-space: nowrap;
  font-weight: 500;
}

.compact-radio {
  display: flex;
  gap: 10px;
}

.pr-2 {
  padding-right: 10px !important;
}

.pl-2 {
  padding-left: 10px !important;
}

.b-card {
  padding: 20px;
}
</style>
